import Phaser from "phaser";
import { Appearance, DEFAULT_APPEARANCE, LimezuAvatar } from "./avatar/LimezuAvatar";
import { Direction, Layer, loadManifest, thumbUrl } from "./avatar/atlas";

/**
 * LimeZu-powered character creator.
 *
 * Replaces the procedural-graphics version from Phase 10/11. The user is
 * shown a tabbed picker of every Character_Generator layer: Body / Eyes /
 * Hair / Outfit / Headgear / Accessory — each tab a scrollable grid of
 * thumbnails generated by `scripts/build-avatar-thumbnails.py`.
 *
 *   - Click a thumb → updates that layer of `appearance` → live preview
 *     reloads via `LimezuAvatar.setAppearance(...)`.
 *   - Direction buttons rotate the preview through 4 facings.
 *   - "Enter the office" persists via PATCH /api/me/appearance and fires
 *     `onDone(appearance)`.
 *
 * Designed for the user's "use everything I paid for" mandate: the picker
 * exposes every body (9), every eye color (7), every hair family + variant
 * (200), every outfit family + variant (132), all headgear (40), all
 * accessories (84). Locked items (priced > 0) appear with 🔒 + price
 * overlay until the player owns them via the shop flow (Phase 12.4).
 */

const API_BASE = (() => {
  if (location.port === "5173") return `${location.protocol}//${location.hostname}:2567`;
  return "";
})();

type CategoryKey =
  | "premade"
  | "body"
  | "eyes"
  | "hair"
  | "outfit"
  | "headgear"
  | "accessory";

interface Category {
  key: CategoryKey;
  label: string;
  layer?: Layer;
  hasNoneOption?: boolean;
}

const CATEGORIES: Category[] = [
  { key: "premade",   label: "Looks" },
  { key: "body",      label: "Body",      layer: "body" },
  { key: "eyes",      label: "Eyes",      layer: "eyes" },
  { key: "hair",      label: "Hair",      layer: "hair" },
  { key: "outfit",    label: "Outfit",    layer: "outfit" },
  { key: "headgear",  label: "Hat",       layer: "headgear", hasNoneOption: true },
  { key: "accessory", label: "Accessory", layer: "accessory", hasNoneOption: true },
];

interface SwatchEntry {
  family: number;
  variant: number;
  thumb: string;
  label: string;
  price: number; // 0 = free; nonzero shows lock overlay until owned
}

export class CharacterCreator {
  private root: HTMLDivElement;
  private panel!: HTMLDivElement;
  private appearance: Appearance;
  private activeCategory: CategoryKey = "premade";
  private previewGame: Phaser.Game | null = null;
  private avatar: LimezuAvatar | null = null;
  private avatarReady = false;
  private direction: Direction = "down";
  private gridContainer!: HTMLDivElement;
  private tabsContainer!: HTMLDivElement;
  private onDone: (a: Appearance) => void;
  private token: string;
  private manifestLoaded = false;
  private manifest: any = null;
  private destroyed = false;
  // Items owned by the player (purchased via shop). For MVP, free-tier items
  // are always considered owned; paid items only after Phase 12.4.
  private ownedSet: Set<string> = new Set();

  constructor(token: string, initial: Appearance, onDone: (a: Appearance) => void) {
    this.token = token;
    this.appearance = { ...DEFAULT_APPEARANCE, ...initial };
    this.onDone = onDone;
    this.root = document.createElement("div");
    this.mount();
    void this.init();
  }

  destroy() {
    this.destroyed = true;
    this.previewGame?.destroy(true);
    this.root.remove();
  }

  private async init() {
    this.manifest = await loadManifest();
    this.manifestLoaded = true;
    // Fetch wallet for owned items if available.
    try {
      const r = await fetch(`${API_BASE}/api/me/wallet`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (r.ok) {
        const data = await r.json();
        for (const inv of data.inventory || []) this.ownedSet.add(inv.item_id);
      }
    } catch (e) {
      // Non-fatal — proceed with free-tier-only.
    }
    this.renderTabs();
    this.renderGrid();
    this.startPreview();
  }

  // ─────────────── DOM ───────────────

  private mount() {
    this.root.id = "character-creator";
    this.root.style.cssText = `
      position: fixed; inset: 0;
      background: linear-gradient(135deg, #1a1430 0%, #2d1b4e 100%);
      z-index: 5000;
      display: flex; align-items: center; justify-content: center;
      font-family: Inter, system-ui, sans-serif;
      color: #f3e8ff;
    `;
    document.body.appendChild(this.root);

    this.panel = document.createElement("div");
    this.panel.style.cssText = `
      width: min(1080px, 96vw);
      max-height: 94vh;
      background: rgba(15, 10, 40, 0.92);
      border: 1px solid #ff2e88;
      box-shadow: 0 0 60px rgba(255, 46, 136, 0.3);
      border-radius: 16px;
      padding: 22px;
      display: grid;
      grid-template-columns: 200px 1fr 1fr;
      grid-template-rows: auto 1fr auto;
      gap: 16px;
      overflow: hidden;
    `;
    this.root.appendChild(this.panel);

    // Title bar
    const title = document.createElement("h1");
    title.textContent = "♥ One Heart · Character Creator";
    title.style.cssText = "grid-column: 1 / 4; margin: 0; color: #ff2e88; letter-spacing: 0.05em; font-size: 22px;";
    this.panel.appendChild(title);

    // Left: tabs
    this.tabsContainer = document.createElement("div");
    this.tabsContainer.style.cssText = "display: flex; flex-direction: column; gap: 6px; min-width: 0;";
    this.panel.appendChild(this.tabsContainer);

    // Centre: live preview canvas
    const previewCol = document.createElement("div");
    previewCol.style.cssText = `
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 10px;
      background: #0d0820;
      border: 1px solid #4c1d95;
      border-radius: 10px;
      padding: 12px;
      min-height: 300px;
    `;
    const previewMount = document.createElement("div");
    previewMount.id = "creator-preview-mount";
    previewMount.style.cssText = "width: 192px; height: 256px; display: flex; align-items: center; justify-content: center; image-rendering: pixelated;";
    previewCol.appendChild(previewMount);

    const dirRow = document.createElement("div");
    dirRow.style.cssText = "display: flex; gap: 6px;";
    for (const dir of ["left", "down", "up", "right"] as Direction[]) {
      const btn = document.createElement("button");
      btn.textContent = { left: "◀", down: "▼", up: "▲", right: "▶" }[dir];
      btn.style.cssText = `
        background: rgba(91, 33, 182, 0.4);
        border: 1px solid #7c3aed; color: #f3e8ff;
        width: 36px; height: 32px; border-radius: 6px;
        cursor: pointer; font-size: 14px;
      `;
      btn.onclick = () => {
        this.direction = dir;
        this.avatar?.setDirection(dir);
      };
      dirRow.appendChild(btn);
    }
    previewCol.appendChild(dirRow);
    this.panel.appendChild(previewCol);

    // Right: swatch grid (scrollable)
    this.gridContainer = document.createElement("div");
    this.gridContainer.style.cssText = `
      overflow-y: auto;
      background: rgba(76, 29, 149, 0.18);
      border: 1px solid #4c1d95;
      border-radius: 10px;
      padding: 12px;
      max-height: 70vh;
    `;
    this.panel.appendChild(this.gridContainer);

    // Bottom: actions
    const actionRow = document.createElement("div");
    actionRow.style.cssText = "grid-column: 1 / 4; display: flex; justify-content: space-between; align-items: center; gap: 12px;";
    const hint = document.createElement("div");
    hint.style.cssText = "color: #c4b5fd; font-size: 12px;";
    hint.textContent = "🔒 items unlock at the matching shop. Free starter set is included.";
    actionRow.appendChild(hint);
    const enter = document.createElement("button");
    enter.textContent = "Enter the office →";
    enter.style.cssText = `
      background: linear-gradient(135deg, #ff2e88 0%, #fb923c 100%);
      color: #1a1430; border: none;
      padding: 12px 28px; border-radius: 999px;
      font-weight: 700; font-size: 14px;
      cursor: pointer; box-shadow: 0 0 18px rgba(255, 46, 136, 0.5);
    `;
    enter.onclick = () => this.save();
    actionRow.appendChild(enter);
    this.panel.appendChild(actionRow);
  }

  private renderTabs() {
    while (this.tabsContainer.firstChild) this.tabsContainer.removeChild(this.tabsContainer.firstChild);
    for (const cat of CATEGORIES) {
      const btn = document.createElement("button");
      btn.textContent = cat.label;
      const active = this.activeCategory === cat.key;
      btn.style.cssText = `
        background: ${active ? "#ff2e88" : "rgba(91, 33, 182, 0.35)"};
        border: 1px solid ${active ? "#ff2e88" : "#4c1d95"};
        color: ${active ? "#1a1430" : "#f3e8ff"};
        padding: 10px 14px;
        border-radius: 8px;
        cursor: pointer;
        text-align: left;
        font-weight: ${active ? "700" : "500"};
        font-size: 13px;
        letter-spacing: 0.04em;
      `;
      btn.onclick = () => {
        this.activeCategory = cat.key;
        this.renderTabs();
        this.renderGrid();
      };
      this.tabsContainer.appendChild(btn);
    }
  }

  private renderGrid() {
    while (this.gridContainer.firstChild) this.gridContainer.removeChild(this.gridContainer.firstChild);
    const cat = CATEGORIES.find((c) => c.key === this.activeCategory);
    if (!cat) return;
    const entries = this.collectEntries(cat);
    const grid = document.createElement("div");
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
      gap: 8px;
    `;
    for (const e of entries) {
      grid.appendChild(this.renderSwatch(cat, e));
    }
    this.gridContainer.appendChild(grid);
  }

  private collectEntries(cat: Category): SwatchEntry[] {
    if (!this.manifestLoaded) return [];
    const out: SwatchEntry[] = [];
    if (cat.hasNoneOption) {
      out.push({ family: 0, variant: 0, thumb: "", label: "(none)", price: 0 });
    }
    const m = this.manifest.packs;

    if (cat.key === "premade") {
      for (const e of m.character_generator.premade) {
        out.push({ family: e.idx, variant: 0, thumb: thumbUrl("premade" as any, e.idx), label: `Look ${e.idx}`, price: 500 + e.idx * 150 });
      }
      return out;
    }
    if (cat.key === "body") {
      for (const e of m.character_generator.bodies) {
        out.push({ family: e.family, variant: 0, thumb: thumbUrl("body", e.family), label: `Body ${e.family}`, price: 0 });
      }
      return out;
    }
    if (cat.key === "eyes") {
      for (const e of m.character_generator.eyes) {
        out.push({ family: e.family, variant: 0, thumb: thumbUrl("eyes", e.family), label: `Eyes ${e.family}`, price: 0 });
      }
      return out;
    }
    if (cat.key === "hair") {
      for (const e of m.character_generator.hairstyles) {
        const price = (e.family <= 5 && e.variant <= 2) ? 0 : (e.family <= 10 ? 100 + e.variant * 25 : 300 + e.family * 5 + e.variant * 30);
        out.push({ family: e.family, variant: e.variant, thumb: thumbUrl("hair", e.family, e.variant), label: `${e.family}.${e.variant}`, price });
      }
      return out;
    }
    if (cat.key === "outfit") {
      for (const e of m.character_generator.outfits) {
        const price = (e.family <= 5 && e.variant <= 2) ? 0 : (e.family <= 12 ? 150 + e.variant * 30 : 400 + e.family * 8 + e.variant * 40);
        const label = e.label ? `${e.label.replace(/_/g, " ")}` : `Outfit ${e.family}.${e.variant}`;
        out.push({ family: e.family, variant: e.variant, thumb: thumbUrl("outfit", e.family, e.variant), label, price });
      }
      return out;
    }
    if (cat.key === "headgear") {
      for (const e of m.headgear) {
        const v = e.variant ?? 0;
        const price = e.family <= 3 ? 0 : 60 + e.family * 30 + v * 40;
        out.push({ family: e.family, variant: v, thumb: thumbUrl("headgear", e.family, v || undefined as any), label: v ? `${e.family}.${v}` : `${e.family}`, price });
      }
      return out;
    }
    if (cat.key === "accessory") {
      for (const e of m.character_generator.accessories) {
        const price = (e.family <= 3 && e.variant <= 2) ? 0 : 100 + e.family * 8 + e.variant * 25;
        const label = e.label ? `${e.label.replace(/_/g, " ")}` : `Acc ${e.family}.${e.variant}`;
        out.push({ family: e.family, variant: e.variant, thumb: thumbUrl("accessory", e.family, e.variant), label, price });
      }
      return out;
    }
    return out;
  }

  private isSelected(cat: Category, e: SwatchEntry): boolean {
    const a = this.appearance;
    switch (cat.key) {
      case "body":      return e.family === a.bodyIdx;
      case "eyes":      return e.family === a.eyesIdx;
      case "hair":      return e.family === a.hairFamily && e.variant === a.hairVariant;
      case "outfit":    return e.family === a.outfitFamily && e.variant === a.outfitVariant;
      case "headgear":  return e.family === a.headgearFamily && e.variant === a.headgearVariant;
      case "accessory": return e.family === a.accessoryFamily && e.variant === a.accessoryVariant;
      case "premade":   return false; // premade applies a full-stack preset, no "selected" state
    }
  }

  private isOwned(cat: Category, e: SwatchEntry): boolean {
    if (e.price === 0 || e.family === 0) return true;
    const slot = cat.layer || cat.key;
    const itemId = e.variant
      ? `${slot}_${String(e.family).padStart(2, "0")}_${String(e.variant).padStart(2, "0")}`
      : `${slot}_${String(e.family).padStart(2, "0")}`;
    return this.ownedSet.has(itemId);
  }

  private renderSwatch(cat: Category, e: SwatchEntry): HTMLDivElement {
    const card = document.createElement("div");
    const selected = this.isSelected(cat, e);
    const owned = this.isOwned(cat, e);
    // Selected swatch: thick pink border + glowing shadow + scale up so the
    // selection is unmistakable. Unselected: thin lavender border.
    card.style.cssText = `
      position: relative;
      width: 72px; height: 96px;
      border: ${selected ? "3px solid #ff2e88" : "2px solid " + (owned ? "rgba(167, 139, 250, 0.5)" : "rgba(91, 33, 182, 0.4)")};
      background: ${selected ? "rgba(255, 46, 136, 0.25)" : "rgba(76, 29, 149, 0.18)"};
      border-radius: 8px;
      cursor: ${owned ? "pointer" : "not-allowed"};
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      overflow: hidden;
      opacity: ${owned ? 1 : 0.55};
      transition: transform 80ms ease, box-shadow 120ms ease;
      box-shadow: ${selected ? "0 0 14px rgba(255, 46, 136, 0.85), inset 0 0 12px rgba(255, 46, 136, 0.25)" : "none"};
      transform: ${selected ? "scale(1.06)" : "none"};
    `;
    if (owned) {
      card.onmouseenter = () => { if (!selected) card.style.transform = "translateY(-2px)"; };
      card.onmouseleave = () => { card.style.transform = selected ? "scale(1.06)" : ""; };
      card.onclick = () => this.selectSwatch(cat, e);
    }
    if (e.thumb) {
      const img = document.createElement("img");
      img.src = e.thumb;
      img.style.cssText = "width: 64px; height: 80px; image-rendering: pixelated; object-fit: contain;";
      img.onerror = () => { img.style.display = "none"; };
      card.appendChild(img);
    } else {
      const noneLabel = document.createElement("span");
      noneLabel.textContent = "—";
      noneLabel.style.cssText = "font-size: 24px; color: #6b7280;";
      card.appendChild(noneLabel);
    }
    // Selected check-mark badge — top-left corner.
    if (selected) {
      const badge = document.createElement("div");
      badge.textContent = "✓";
      badge.style.cssText = `
        position: absolute; top: 3px; left: 3px;
        width: 20px; height: 20px;
        background: #ff2e88; color: white;
        font-size: 14px; font-weight: 800;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 0 8px rgba(255, 46, 136, 0.7);
      `;
      card.appendChild(badge);
    }
    if (!owned && e.price > 0) {
      const lock = document.createElement("div");
      lock.style.cssText = `
        position: absolute; top: 4px; right: 4px;
        background: rgba(0, 0, 0, 0.7);
        color: #fde047;
        font-size: 9px; font-weight: 700;
        padding: 2px 5px; border-radius: 4px;
        letter-spacing: 0.04em;
      `;
      lock.textContent = `🔒 ${e.price}`;
      card.appendChild(lock);
    }
    return card;
  }

  private async selectSwatch(cat: Category, e: SwatchEntry) {
    if (cat.key === "premade") {
      // For premade, hot-load the preset by mapping idx → reasonable defaults.
      // (MVP: just bump the preview to show that picking a premade does something —
      // proper preset application can come from a per-premade JSON in 12.4.)
      this.appearance = {
        ...DEFAULT_APPEARANCE,
        outfitFamily: ((e.family - 1) % 33) + 1,
        outfitVariant: ((e.family - 1) % 4) + 1,
        hairFamily: ((e.family - 1) % 29) + 1,
        hairVariant: ((e.family - 1) % 7) + 1,
      };
      await this.avatar?.setAppearance(this.appearance);
      this.renderGrid();
      return;
    }

    const update: Partial<Appearance> = {};
    switch (cat.key) {
      case "body":      update.bodyIdx = e.family; break;
      case "eyes":      update.eyesIdx = e.family; break;
      case "hair":      update.hairFamily = e.family; update.hairVariant = e.variant; break;
      case "outfit":    update.outfitFamily = e.family; update.outfitVariant = e.variant; break;
      case "headgear":  update.headgearFamily = e.family; update.headgearVariant = e.variant; break;
      case "accessory": update.accessoryFamily = e.family; update.accessoryVariant = e.variant; break;
    }
    Object.assign(this.appearance, update);
    if (this.avatar) await this.avatar.setAppearance(update);
    this.renderGrid();
  }

  // ─────────────── Phaser preview ───────────────

  private startPreview() {
    const mount = document.getElementById("creator-preview-mount");
    if (!mount || this.previewGame) return;
    const self = this;
    class PreviewScene extends Phaser.Scene {
      preload() {}
      create() {
        if (self.destroyed) return;
        // Avatar renders at scale 2 (set in LimezuAvatar). Camera zoom 1.4
        // gives a comfortable margin so hats + accessories don't clip the
        // top of the preview canvas. Centred so feet sit in lower third.
        this.cameras.main.setZoom(1.4);
        this.cameras.main.centerOn(0, -50);
        self.avatar = new LimezuAvatar(this, 0, 30, self.appearance);
        void self.avatar.start().then(() => {
          self.avatarReady = true;
          self.avatar?.setDirection(self.direction);
        });
      }
      update(_t: number, dt: number) {
        self.avatar?.update(dt);
      }
    }
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: 192,
      height: 256,
      parent: mount,
      transparent: true,
      pixelArt: true,
      scene: PreviewScene,
    };
    this.previewGame = new Phaser.Game(config);
  }

  // ─────────────── Save ───────────────

  private async save() {
    try {
      await fetch(`${API_BASE}/api/me/appearance`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify(this.appearance),
      });
    } catch (e) {
      console.warn("[creator] save failed", e);
    }
    const final = this.appearance;
    this.destroy();
    this.onDone(final);
  }
}
